import { useCallback, useEffect, useState } from "react";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import axios from "../../api/axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import {
  AddImageButton,
  CancelButton,
  DeleteButton,
  EditImageButton,
  SaveButton,
} from "../buttons/Buttons";
import cloudinaryConfig from "../../config/cloudinary.config";
import imageCompression from "browser-image-compression";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";

const WEBSITE_URL = "/image_url";
const title = "certificates";

export const Certificates = () => {
  const { username, auth } = useAuth();
  const { t } = useTranslation();

  const galleryTitle = "gallery-" + title;
  const maxImages = 10;

  const [imageObjects, setImageObjects] = useState([]);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [images, setImages] = useState([]);
  const [status, setStatus] = useState("");
  const [lastIndex, setLastIndex] = useState(0);
  const [nofImagesInDB, setNofImagesInDB] = useState(-1);
  const [nofImagesToUpload, setNofImagesToUpload] = useState(0);
  const [uploadLegal, setUploadLegal] = useState(false);

  const updateGallery = useCallback(async () => {
    const url = WEBSITE_URL + "/gallery";
    try {
      const response = await axios.get(url, {
        params: { title: galleryTitle, username },
      });
      if (response.data && response.data.length > 0) {
        const images = response.data
          .sort((a, b) => a.public_id.localeCompare(b.public_id))
          .map((item, idx) => ({
            url: item.url,
            public_id: item.public_id,
            idx,
          }));
        setLastIndex(
          Math.max(
            ...images.map((obj) => Number(obj.public_id.split("-").pop()))
          )
        );
        setImageObjects(images);
        setNofImagesInDB(images.length);
      }
    } catch (err) {
      console.error("Error loading certificates:", err);
    }
  }, [galleryTitle, username]);

  useEffect(() => {
    updateGallery();
  }, [updateGallery]);

  useEffect(() => {
    const uploadAllowed = nofImagesInDB + nofImagesToUpload <= maxImages;
    setUploadLegal(uploadAllowed);
    if (!uploadAllowed) {
      setStatus(
        `Maximum (${maxImages}) images reached. You can upload only ${
          maxImages - nofImagesInDB
        } more.`
      );
    } else if (nofImagesInDB === maxImages) {
      setStatus(`Maximum (${maxImages}) images reached.`);
    } else {
      setStatus("Please choose a file to upload.");
    }
  }, [nofImagesInDB, nofImagesToUpload]);

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    const options = { maxSizeMB: 0.3, maxWidthOrHeight: 1920 };
    const newImages = [];

    for (const file of files) {
      try {
        const compressed =
          file.size > options.maxSizeMB * 1024 * 1024
            ? await imageCompression(file, options)
            : file;
        const reader = new FileReader();
        reader.readAsDataURL(compressed);
        reader.onload = () => {
          newImages.push(reader.result);
          if (newImages.length === files.length) {
            setImages(newImages);
          }
        };
      } catch (err) {
        console.error("Compression failed:", err);
      }
    }
    setNofImagesToUpload(files.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("Uploading...");
    const folder = `${username}/${galleryTitle}`;

    for (let i = 0; i < images.length; i++) {
      const newIndex = lastIndex + i + 1;
      const signRes = await axios.get("/signuploadform", {
        params: { folder, publicID: `${title}-${newIndex}` },
      });

      const signData = signRes.data;
      const url =
        cloudinaryConfig.BASE_URL +
        signData.cloudName +
        cloudinaryConfig.UPLOAD_SUFFIX;

      const formData = new FormData();
      formData.append("api_key", signData.apiKey);
      formData.append("cloud_name", signData.cloudName);
      formData.append("file", images[i]);
      formData.append("folder", folder);
      formData.append("public_id", `${title}-${newIndex}`);
      formData.append("signature", signData.signature);
      formData.append("timestamp", signData.timestamp);

      const uploadRes = await axios.post(url, formData);
      await axios.put("/image_url", {
        username,
        title: galleryTitle,
        url: uploadRes.data.secure_url,
        public_id: uploadRes.data.public_id,
      });
    }

    setImages([]);
    setAdding(false);
    await updateGallery();
    setStatus("Upload successful!");
  };

  const handleDelete = async (e) => {
    const public_id = e.target.id;
    const signRes = await axios.get("/signuploadform", {
      params: { publicID: public_id },
    });
    const signData = signRes.data;
    const url =
      cloudinaryConfig.BASE_URL +
      signData.cloudName +
      cloudinaryConfig.DELETE_SUFFIX;

    await axios.post(url, {
      signature: signData.signature,
      api_key: signData.apiKey,
      cloud_name: signData.cloudName,
      timestamp: signData.timestamp,
      public_id,
      invalid: true,
    });

    await axios.delete(WEBSITE_URL, {
      data: { username, title: galleryTitle, public_id },
    });

    await updateGallery();
  };
  
  return (
    <>
      {(imageObjects.length > 0 || auth?.role) && (
        <div className="certificates-main">
          <h2 style={{ marginBottom: "30px" }}>
            {t("home_page.certificates")}
          </h2>

          {editing ? (
            <div>
              {!adding && (
                <>
                  <AddImageButton
                    buttonText={t("button.add")}
                    onClick={() => setAdding(true)}
                    disable={!uploadLegal}
                  />
                  <CancelButton
                    buttonText={t("button.cancel")}
                    onClick={() => {
                      setEditing(false);
                      setStatus("");
                    }}
                  />
                </>
              )}
              {adding && (
                <form onSubmit={handleSubmit}>
                  <input type="file" multiple onChange={handleFileUpload} />
                  <SaveButton
                    buttonText={t("button.upload")}
                    type="submit"
                    enabled={uploadLegal}
                  />
                  <CancelButton
                    buttonText={t("button.cancel")}
                    onClick={() => {
                      setAdding(false);
                      setStatus("");
                    }}
                  />
                  {status && <div className="status-info">{status}</div>}
                </form>
              )}
            </div>
          ) : (
            auth?.role && (
              <EditImageButton
                buttonText={t("button.edit_gallery")}
                onClick={() => setEditing(true)}
              />
            )
          )}

          <div style={{ paddingTop: "20px", paddingBottom: "20px" }}>
            <Swiper
              modules={[Autoplay, Pagination, Navigation]}
              spaceBetween={20}
              slidesPerView={1}
              loop={true}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              pagination={{ clickable: true }}
              navigation={true}
            >
              {imageObjects.map((image, idx) => (
                <SwiperSlide key={idx}>
                  <div className="certificate-img-wrapper">
                    <img
                      className="certificate-img"
                      src={image.url}
                      alt="Certificate"
                    />
                    {editing && (
                      <DeleteButton
                        buttonText={t("button.delete")}
                        onClick={handleDelete}
                        id={image.public_id}
                      />
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}
    </>
  );
};
